import { Filter, Play } from 'lucide-react'
import { cn } from '@/utils/cn'

export type TemporalMode = 'filter' | 'playback'

export interface ModeToggleProps {
  value: TemporalMode
  onChange: (next: TemporalMode) => void
  className?: string
}

/**
 * Compact segmented toggle used on the map view to flip the bottom
 * temporal strip between two scopes:
 *
 *  - **Filter**   axis spans ALL temporal entities (animated + pinned).
 *                 Handles hide entities outside the window.
 *  - **Playback** axis spans only animated entities. Transport + playhead
 *                 appear so playback never sits in dead zones.
 *
 * Lives in the strip's left cell (next to the view chip) so it doesn't
 * jump across the bar when the user flips between modes.
 */
export function ModeToggle({ value, onChange, className }: ModeToggleProps) {
  return (
    <span
      role="tablist"
      aria-label="Temporal control mode"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md border border-border bg-card/70 p-0.5',
        className
      )}
    >
      <ToggleButton
        icon={<Filter className="h-3 w-3" />}
        label="Filter"
        active={value === 'filter'}
        onClick={() => onChange('filter')}
      />
      <ToggleButton
        icon={<Play className="h-3 w-3" />}
        label="Playback"
        active={value === 'playback'}
        onClick={() => onChange('playback')}
      />
    </span>
  )
}

function ToggleButton({
  icon,
  label,
  active,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      role="tab"
      type="button"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        // Sized to read as the strip's heading when it lives in the
        // title slot — same font/tracking as "TIME RANGE" used elsewhere.
        'inline-flex items-center gap-1.5 rounded-sm px-2 py-2 transition-colors md:py-1',
        'font-mono text-[10.5px] font-medium uppercase tracking-[0.16em]',
        active
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      )}
    >
      {icon}
      {label}
    </button>
  )
}
